import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import type { Comic } from '@/mocks/comics';

type SortKey = 'latest' | 'popular' | 'rating';

function parseViews(v: string): number {
  if (v.includes('亿')) return parseFloat(v) * 10000;
  if (v.includes('万')) return parseFloat(v);
  return 0;
}

export default function CategoryView({ type, label, tagline, comics }: { type: 'guoman' | 'riman'; label: string; tagline: string; comics: Comic[] }) {
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();
  const [genre, setGenre] = useState('全部');
  const [sort, setSort] = useState<SortKey>('latest');

  const genres = useMemo(() => {
    const set = new Set<string>();
    comics.forEach((c) => c.genres.forEach((g) => set.add(g)));
    return ['全部', ...Array.from(set)];
  }, [comics]);

  const filtered = useMemo(() => {
    let list = comics;
    if (genre !== '全部') list = list.filter((c) => c.genres.includes(genre));
    if (q) list = list.filter((c) => c.title.includes(q) || c.genres.some((g) => g.includes(q)));
    const sorted = [...list];
    if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    if (sort === 'popular') sorted.sort((a, b) => parseViews(b.views) - parseViews(a.views));
    if (sort === 'latest') sorted.sort((a, b) => b.year - a.year);
    return sorted;
  }, [comics, genre, q, sort]);

  const structured = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://oiutc07.cn/' },
          { '@type': 'ListItem', position: 2, name: label, item: `https://oiutc07.cn/${type}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${label}频道 - 虫虫漫画`,
        url: `https://oiutc07.cn/${type}`,
        mainEntity: { '@type': 'ItemList', name: `${label}作品列表`, itemListElement: filtered.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.title, url: `https://oiutc07.cn/comic/${c.id}` })) },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }} />
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <nav className="text-sm text-foreground-500" aria-label="面包屑">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground-700" aria-current="page">{label}频道</li>
          </ol>
        </nav>

        <div className="mt-5">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">
            {label}<span className="text-primary-500">频道</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600">{tagline}</p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {genres.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGenre(g)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                genre === g ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-foreground-500">
            {q && <span>关键词「<span className="text-primary-600 font-medium">{q}</span>」共 </span>}
            找到 <span className="font-medium text-foreground-900">{filtered.length}</span> 部作品
          </p>
          <div className="inline-flex items-center rounded-full border border-background-200 bg-background-100 p-1">
            {([
              { key: 'latest', label: '最新上架' },
              { key: 'popular', label: '人气最高' },
              { key: 'rating', label: '评分最高' },
            ] as { key: SortKey; label: string }[]).map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSort(s.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  sort === s.key ? 'bg-background-50 text-primary-600 shadow-soft' : 'text-foreground-500 hover:text-primary-600'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
            <p className="text-foreground-500">没有找到匹配的作品，换个关键词试试</p>
            <Link to={type === 'guoman' ? '/guoman' : '/riman'} className="rounded-full bg-primary-500 px-6 py-2 text-sm text-background-50 whitespace-nowrap hover:bg-primary-600">
              清除筛选
            </Link>
          </div>
        )}
      </section>
    </>
  );
}